/**
 * Author: Claude Code
 * Version: v3.0.84
 * Modified: 2025-09-09 Beijing Time
 * 
 * GameManager - 游戏流程协调器
 * 协调GameContainer和AgentRegistrationManager，管理游戏生命周期
 */

import { GameContainer } from './GameContainer';
import { AgentRegistrationManager, AgentConfiguration } from './systems/AgentRegistrationManager';
import webSocketService from '../services/websocket/WebSocketService';
import { GAME_CONFIG, AGENT_CONFIG, SPAWN_MAP_BOUNDS, GAME_PHASES } from './config/gameConfig';

export interface GameManagerEventHandlers {
  onPhaseChange?: (phase: string) => void;
  onAgentRegistered?: (agentId: string, username: string) => void;
  onGameStart?: () => void;
  onGameEnd?: () => void;
  onError?: (error: Error) => void;
}

export class GameManager {
  private gameContainer: GameContainer;
  private agentRegistrationManager: AgentRegistrationManager;
  private currentPhase: string = GAME_PHASES.REGISTRATION;
  private eventHandlers: GameManagerEventHandlers = {};
  private isInitialized: boolean = false;
  private currentRoomId: string | null = null;

  constructor() {
    this.gameContainer = GameContainer.getInstance();
    this.agentRegistrationManager = new AgentRegistrationManager(AGENT_CONFIG.MAX_AGENTS);
    this.initializeEventListeners();
  }

  // Initialize WebSocket and game event listeners
  private initializeEventListeners(): void {
    // Game phase events
    webSocketService.on('game:phase_change', (data: any) => {
      this.handlePhaseChange(data.phase);
    });

    // Registration events
    webSocketService.on('game:registration_opened', () => {
      this.startRegistration();
    });

    webSocketService.on('game:registration_closed', () => {
      this.endRegistration();
    });

    // Game start event
    webSocketService.on('game:battle_start', () => {
      this.startBattle();
    });

    // Error handling
    webSocketService.on('error', (error: any) => {
      this.handleError(new Error(error.message || 'WebSocket error'));
    });

    // Scene transition events
    this.gameContainer.onSceneTransition((transition) => {
      console.log(`Scene transition: ${transition.from} -> ${transition.to}`);
    });
  }

  // Initialize the game manager
  async initialize(opts?: { connect?: boolean }): Promise<void> {
    if (this.isInitialized) {
      console.warn('GameManager already initialized');
      return;
    }

    try {
      // Initialize GameContainer
      this.gameContainer.initialize(GAME_CONFIG);

      // Update agent registration manager with map bounds
      this.agentRegistrationManager.updateMapBounds(SPAWN_MAP_BOUNDS);

      // Connect to WebSocket service (optional, default true)
      const shouldConnect = opts?.connect !== undefined ? !!opts.connect : true;
      if (shouldConnect) {
        if (!webSocketService.isConnected()) {
          await webSocketService.connect();
        }
      }

      this.isInitialized = true;
      console.log('GameManager initialized successfully');
    } catch (error) {
      const gameError = error instanceof Error ? error : new Error('Initialization failed');
      this.handleError(gameError);
      throw gameError;
    }
  }

  // Start the game
  start(): void {
    if (!this.isInitialized) {
      throw new Error('GameManager not initialized');
    }

    try {
      this.gameContainer.start();
      this.setPhase(GAME_PHASES.REGISTRATION);
      this.notifyEventHandlers('onGameStart');
      console.log('Game started');
    } catch (error) {
      const gameError = error instanceof Error ? error : new Error('Failed to start game');
      this.handleError(gameError);
    }
  }

  // Register an agent
  registerAgent(username: string, configuration: AgentConfiguration): string {
    try {
      const agentId = this.agentRegistrationManager.registerAgent(username, configuration);
      
      // Notify event handlers
      this.notifyEventHandlers('onAgentRegistered', agentId, username);
      
      // Update game state (Registry 注入仅用于传入 DataManager；状态更新需使用 updateGameState)
      webSocketService.updateGameState('agents', this.agentRegistrationManager.getRegistrationStats());
      
      return agentId;
    } catch (error) {
      const registrationError = error instanceof Error ? error : new Error('Agent registration failed');
      this.handleError(registrationError);
      throw registrationError;
    }
  }

  // Get registration statistics
  getRegistrationStats() {
    return this.agentRegistrationManager.getRegistrationStats();
  }

  // Start registration phase
  private startRegistration(): void {
    this.setPhase(GAME_PHASES.REGISTRATION);
    this.agentRegistrationManager.clearRegistrations();
    console.log('Registration phase started');
  }

  // End registration phase
  private endRegistration(): void {
    this.setPhase(GAME_PHASES.PREPARATION);
    this.agentRegistrationManager.assignSpawnPositions();
    
    // Send agent data to game scene
    const agents = this.agentRegistrationManager.getAllAgents();
    this.gameContainer.setSceneData('registeredAgents', agents);
    
    console.log('Registration phase ended, agents prepared for battle');
  }

  // Start battle phase
  private startBattle(): void {
    this.setPhase(GAME_PHASES.BATTLE);

    try {
      // Switch to BattleScene with agent data
      const agents = this.agentRegistrationManager.getAllAgents();
      this.gameContainer.switchScene('BattleScene', { 
        agents,
        gamePhase: GAME_PHASES.BATTLE,
        roomId: this.currentRoomId || undefined
      });

      console.log('Battle phase started');
    } catch (error) {
      const battleError = error instanceof Error ? error : new Error('Failed to start battle');
      this.handleError(battleError);
    }
  }

  // Handle phase changes
  private handlePhaseChange(phase: string): void {
    this.setPhase(phase);
  }

  // Set current game phase
  private setPhase(phase: string): void {
    const previousPhase = this.currentPhase;
    this.currentPhase = phase;
    
    // Update game state (通过 WebSocketService.updateGameState 写入 Registry)
    webSocketService.updateGameState('gamePhase', phase);
    
    // Notify event handlers
    this.notifyEventHandlers('onPhaseChange', phase);
    
    console.log(`Game phase changed: ${previousPhase} -> ${phase}`);
  }

  // Get current game phase
  getCurrentPhase(): string {
    return this.currentPhase;
  }

  // Switch to a different scene
  switchScene(sceneName: string, data?: any): void {
    try {
      this.gameContainer.switchScene(sceneName, data);
    } catch (error) {
      const sceneError = error instanceof Error ? error : new Error('Scene switch failed');
      this.handleError(sceneError);
    }
  }

  // Pause the game
  pause(): void {
    this.gameContainer.pause();
  }

  // Resume the game
  resume(): void {
    this.gameContainer.resume();
  }

  // Register event handlers
  onEvent(handlers: GameManagerEventHandlers): void {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };
  }

  // Notify event handlers
  private notifyEventHandlers(eventName: keyof GameManagerEventHandlers, ...args: any[]): void {
    const handler = this.eventHandlers[eventName];
    if (handler && typeof handler === 'function') {
      try {
        handler(...args);
      } catch (error) {
        console.error(`Event handler error (${eventName}):`, error);
      }
    }
  }

  // Handle errors
  private handleError(error: Error): void {
    console.error('GameManager error:', error);
    
    // Notify error handler
    this.notifyEventHandlers('onError', error);
    
    // Try to recover
    try {
      this.gameContainer.handleError(error);
    } catch (recoveryError) {
      console.error('Failed to recover from error:', recoveryError);
    }
  }

  // Get game status
  getGameStatus(): {
    isInitialized: boolean;
    isRunning: boolean;
    currentPhase: string;
    registrationStats: any;
    currentScene: string;
  } {
    return {
      isInitialized: this.isInitialized,
      isRunning: this.gameContainer.isGameRunning(),
      currentPhase: this.currentPhase,
      registrationStats: this.agentRegistrationManager.getRegistrationStats(),
      currentScene: this.gameContainer.getCurrentScene()
    };
  }

  // Destroy the game manager
  destroy(): void {
    try {
      this.agentRegistrationManager.clearRegistrations();
      this.gameContainer.destroy();
      this.isInitialized = false;
      this.currentPhase = GAME_PHASES.REGISTRATION;
      this.currentRoomId = null;

      console.log('GameManager destroyed');
    } catch (error) {
      console.error('Error destroying GameManager:', error);
    }
  }

  // v3.2.143: Expose scene data setter for room-specific metadata (e.g., roomId)
  setSceneData(key: string, value: any): void {
    this.gameContainer.setSceneData(key, value);
  }

  // v3.2.143: Track current room id for BattleScene handshakes
  setCurrentRoomId(roomId: string | null): void {
    this.currentRoomId = roomId;
    if (roomId) {
      this.setSceneData('roomId', roomId);
    }
  }
}

// Export singleton instance
let gameManagerInstance: GameManager | null = null;

export function getGameManager(): GameManager {
  if (!gameManagerInstance) {
    gameManagerInstance = new GameManager();
  }
  return gameManagerInstance;
}

export default getGameManager();
